import { useState, useRef, useCallback, useEffect } from 'react';
import type { TransitionEvent } from 'react';
import { css, keyframes } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Text, useTooltip } from '@frontend/design-system';
import {
  INLINE_WIDTH,
  INLINE_WIDTH_MEDIUM,
  INLINE_WIDTH_SMALL,
  POPOVER_WIDTH,
  POPOVER_HEIGHT,
  IDLE_TRANSITION,
  CLOSING_TRANSITION,
  expandBar,
} from './smart-draft.constants';
import type { SmartDraftResultBarProps, SmartDraftVariant } from './smart-draft.types';

// ── Shared pseudo-element arrow (border + fill) ────────────────────
const arrowBase = {
  content: '""',
  position: 'absolute',
  width: 16,
  height: 8,
  left: '50%',
  transform: 'translateX(-50%)',
  clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
} as const;

const arrowBorder = css({
  '&::before': { ...arrowBase, bottom: -8, backgroundColor: theme.colors.neutral50 },
});

const arrowFill = css({
  '&::after': { ...arrowBase, bottom: -7.5, backgroundColor: theme.colors.white },
});

// ── Inline result-bar width helpers ────────────────────────────────
const inlineWidthStyles = (closing: boolean, width: number) => ({
  maxWidth: closing ? 32 : width,
  animation: closing ? undefined : `${expandBar(width)} 400ms cubic-bezier(0.4, 0, 0.2, 1)`,
});

// ── Container styles ───────────────────────────────────────────────
const inlineContainerStyles = (closing: boolean) =>
  css({
    display: 'flex',
    alignItems: 'center',
    height: 32,
    marginLeft: theme.spacing(0.5),
    padding: theme.spacing(0, 0.75),
    borderRadius: theme.borderRadius.large,
    backgroundColor: theme.colors.white,
    border: `0.5px solid ${theme.colors.neutral20}`,
    overflow: 'hidden',
    ...inlineWidthStyles(closing, INLINE_WIDTH),
    transition: closing ? CLOSING_TRANSITION : IDLE_TRANSITION,
    [`@media (max-width: ${breakpoints.medium.max}px)`]: inlineWidthStyles(closing, INLINE_WIDTH_MEDIUM),
    [`@media (max-width: ${breakpoints.small.max}px)`]: inlineWidthStyles(closing, INLINE_WIDTH_SMALL),
    '.smart-draft-action': {
      width: 0,
      padding: 0,
      opacity: 0,
      pointerEvents: 'none',
    },
    '.smart-draft-divider': {
      opacity: 0,
    },
    '&:hover, &:focus-within': {
      backgroundColor: theme.colors.neutral5,
      border: `0.5px solid ${theme.colors.neutral40}`,
      '.smart-draft-action': {
        width: 16,
        padding: theme.spacing(0.75),
        gap: theme.spacing(0.5),
        opacity: 1,
        pointerEvents: 'auto',
      },
      '.smart-draft-divider': {
        opacity: 1,
      },
    },
  });

const mobileContainerStyles = css(
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: POPOVER_WIDTH,
    maxWidth: POPOVER_WIDTH,
    minHeight: POPOVER_HEIGHT,
    height: POPOVER_HEIGHT,
    gap: theme.spacing(0.5),
    borderRadius: 100,
    backgroundColor: theme.colors.white,
    border: `0.5px solid ${theme.colors.neutral20}`,
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
      backgroundColor: theme.colors.neutral5,
      borderColor: theme.colors.neutral40,
      '&::after': {
        backgroundColor: theme.colors.neutral5,
      },
    },
  },
  arrowBorder,
  arrowFill
);

// ── Text & divider ─────────────────────────────────────────────────
const textStyles = css({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: theme.colors.neutral70,
  cursor: 'pointer',
});

const dividerStyles = (variant: SmartDraftVariant) =>
  css({
    height: 16,
    width: variant === 'mobile' ? 1 : 0.5,
    backgroundColor: theme.colors.neutral20,
    flexShrink: 0,
    transition: 'opacity 200ms ease',
    marginRight: theme.spacing(0.25),
  });

// ── Action icon ────────────────────────────────────────────────────
const popIn = keyframes`
  from { opacity: 0; transform: scale(0.4); }
  to   { opacity: 1; transform: scale(1); }
`;

// alwaysVisible = mobile buttons (always shown); delay staggers the three icons
const actionIconStyles = (alwaysVisible?: boolean, delay = 0) =>
  css({
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    cursor: 'pointer',
    boxSizing: 'content-box',
    overflow: 'hidden',
    width: alwaysVisible ? 16 : 0,
    padding: alwaysVisible ? theme.spacing(0.5) : 0,
    opacity: alwaysVisible ? 1 : 0,
    pointerEvents: alwaysVisible ? 'auto' : 'none',
    transition: 'width 200ms ease, padding 200ms ease, opacity 200ms ease',
    color: theme.colors.neutral70,
    borderRadius: theme.borderRadius.small,
    '&:hover': {
      color: theme.colors.neutral90,
      backgroundColor: theme.colors.neutral10,
    },
    ...(alwaysVisible && {
      animation: `${popIn} 200ms cubic-bezier(0.34, 1.56, 0.64, 1) ${delay}ms both`,
    }),
  });

// ── Component ──────────────────────────────────────────────────────
export const SmartDraftResultBar = ({
  text,
  onTextClick,
  onAccept,
  onTextMouseOver,
  onTextMouseLeave,
  onRedo,
  onClose,
  variant = 'inline',
}: SmartDraftResultBarProps) => {
  const { t } = useTranslation('thread-sending-area');
  const [closing, setClosing] = useState(false);
  const closingAction = useRef<(() => void) | null>(null);
  const isInline = variant === 'inline';

  const handleDismiss = useCallback(
    (action: () => void) => {
      if (!isInline) {
        action();
        return;
      }
      setClosing(true);
      closingAction.current = action;
    },
    [isInline]
  );

  const handleTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLDivElement>) => {
      if (event.propertyName === 'max-width' && closing && closingAction.current) {
        closingAction.current();
        closingAction.current = null;
      }
    },
    [closing]
  );

  useEffect(() => {
    if (!isInline && closing) {
      setClosing(false);
      closingAction.current = null;
    }
  }, [closing, isInline]);

  const {
    Tooltip: TextTooltip,
    tooltipProps: textTooltipProps,
    triggerProps: textTriggerProps,
  } = useTooltip({ placement: 'top', hoverDelay: 1000 });
  const {
    Tooltip: RedoTooltip,
    tooltipProps: redoTooltipProps,
    triggerProps: redoTriggerProps,
  } = useTooltip({ placement: 'top', hoverDelay: 1000 });
  const {
    Tooltip: CloseTooltip,
    tooltipProps: closeTooltipProps,
    triggerProps: closeTriggerProps,
  } = useTooltip({ placement: 'top', hoverDelay: 1000 });

  return (
    <>
      <div
        css={isInline ? inlineContainerStyles(closing) : mobileContainerStyles}
        onClick={isInline ? onTextClick : undefined}
        onMouseEnter={onTextMouseOver}
        onMouseLeave={onTextMouseLeave}
        {...(isInline ? { onTransitionEnd: handleTransitionEnd } : {})}
      >
        {isInline && (
          <>
            <span
              {...textTriggerProps}
              css={{ overflow: 'hidden' }}
              data-trackingid={`${InboxPrefixes.Thread}-smart-draft-inline-insert-btn`}
            >
              <Text size='small' css={textStyles}>
                {text}
              </Text>
            </span>
            <div className='smart-draft-divider' css={dividerStyles(variant)} />
          </>
        )}
        {!isInline && onAccept && (
          <span
            className='smart-draft-action'
            css={actionIconStyles(true, 0)}
            data-trackingid={`${InboxPrefixes.Thread}-smart-draft-mobile-insert-btn`}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              onAccept();
            }}
            aria-label={t('Insert smart reply')}
          >
            <Icon name='check-small' size={16} />
          </span>
        )}
        <span
          {...(isInline ? redoTriggerProps : {})}
          className='smart-draft-action'
          css={actionIconStyles(!isInline, 60)}
          data-trackingid={`${InboxPrefixes.Thread}-smart-draft-redo-btn`}
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
            handleDismiss(onRedo);
          }}
          aria-label={t('Redo smart reply')}
        >
          <Icon name='refresh' size={16} />
        </span>
        <span
          {...(isInline ? closeTriggerProps : {})}
          className='smart-draft-action'
          css={actionIconStyles(!isInline, 120)}
          data-trackingid={`${InboxPrefixes.Thread}-smart-draft-close-btn`}
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
            handleDismiss(onClose);
          }}
          aria-label={t('Close')}
        >
          <Icon name='x-small' size={16} />
        </span>
      </div>
      {isInline && (
        <>
          <TextTooltip {...textTooltipProps}>{t('Click to insert smart reply')}</TextTooltip>
          <RedoTooltip {...redoTooltipProps}>{t('Redo smart reply')}</RedoTooltip>
          <CloseTooltip {...closeTooltipProps}>{t('Close')}</CloseTooltip>
        </>
      )}
    </>
  );
};
